import {
    AttachInternals,
    Component,
    ComponentInterface,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Method,
    Prop,
    State,
    Watch
} from "@stencil/core";
import { limitSlotElements } from "../../utils/limitSlotElements";
import { getValidity } from "./validation";

/**
 * General Input component for multiple types of inputs
 * @slot (default) - custom label content
 * @slot (unit) - display a unit of measurement. Must be a ux-unit component
 * @slot (icon) - display an icon. Must be a ux-icon component
 * @slot (hint) - display a hint text that supplements the label text
 */
@Component({
    tag: "ux-input",
    styleUrl: "input.scss",
    shadow: { delegatesFocus: true },
    formAssociated: true
})
export class Input implements ComponentInterface {
    private inputId = "input"; // id is scoped via shadow dom
    private internalInput: HTMLInputElement;
    private hasUnit: boolean;

    @Element() host!: HTMLUxInputElement;

    @AttachInternals() private internals: ElementInternals;

    /** Label for the input */
    @Prop() readonly label: string = "";
    /** Type of input */
    @Prop() readonly type: "text" | "number" | "email" | "password" | "search" | "url" | "tel" | "hidden" = "text";
    /** Placeholder for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly placeholder: string = undefined;
    /** name attribute for the input */
    @Prop() readonly name: string = "";
    /** Disabled attribute for the input */
    @Prop({ reflect: true }) readonly disabled = false;
    /** required attribute for the input */
    @Prop({ reflect: true }) readonly required = false;
    /** readonly attribute for the input */
    @Prop() readonly readonly: boolean = false;
    /** minlength attribute for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly minlength: number = undefined;
    /** maxlength attribute for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly maxlength: number = undefined;
    /** min attribute for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly min: number = undefined;
    /** max attribute for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly max: number = undefined;
    /** step attribute for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly step: number = undefined;
    /** Value for the input */
    @Prop({
        mutable: true
    })
    value = "";
    /** spellcheck attribute for the input */

    // @ts-expect-error Check for typing
    // eslint-disable-next-line @stencil-community/reserved-member-names
    @Prop() readonly spellcheck: boolean = undefined;
    /** autocapitalize attribute for the input */
    // @ts-expect-error Check for typing
    // eslint-disable-next-line @stencil-community/reserved-member-names
    @Prop() readonly autocapitalize: string = undefined;
    /** autocomplete attribute for the input */
    // @ts-expect-error Check for typing
    @Prop() readonly autocomplete: string = undefined;
    /** autofocus attribute for the input */
    // eslint-disable-next-line @stencil-community/reserved-member-names
    @Prop() readonly autofocus: boolean = false;
    /** inputmode attribute for the input */
    // @ts-expect-error Check for typing
    // eslint-disable-next-line @stencil-community/reserved-member-names
    @Prop() readonly inputmode: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" = undefined;
    /** adds a clear button that empties the input value */
    @Prop() readonly clearable: boolean = false;
    /** indicates if the input value is invalid */
    @Prop({ reflect: true }) readonly invalid = false;
    /** indicates the ValidityState of the input */
    // @ts-expect-error Check for typing
    @Prop({ mutable: true }) validity: ValidityState = undefined;

    /** Emitted when a keyboard input occurred. */
    @Event() uxInput!: EventEmitter<string>;
    /** Emitted when the value has changed . */
    @Event() uxInputChange!: EventEmitter<string>;
    /** Emitted when the icon slot was clicked. Use e.preventDefault() to disable default behavior (focus the input) */
    @Event({
        cancelable: true
    })
    uxIconClick!: EventEmitter<MouseEvent>;
    /** Emitted when the clear button was clicked. Use e.preventDefault() to disable default behavior (clear and focus input) */
    @Event({
        cancelable: true
    })
    uxClear!: EventEmitter<MouseEvent>;

    /** Indicates if the component has focus or not */
    @State() hasFocus = false;

    /** Returns the native input element */
    @Method()
    async getInputElement(): Promise<HTMLInputElement> {
        return this.internalInput;
    }

    private handleFocus = () => {
        this.hasFocus = true;
    };

    private handleBlur = (event: FocusEvent) => {
        // only emit blur if the whole component loses focus
        if (!this.host.contains(event.relatedTarget as HTMLElement)) {
            this.hasFocus = false;
        }
    };

    private getValidationMessage() {
        return this.internalInput?.validationMessage || "";
    }

    @Watch("invalid")
    private setValidity() {
        this.validate(this.invalid);
    }

    @Watch("required")
    private setRequired() {
        this.internals.ariaRequired = `${this.required}`;
    }

    @Watch("disabled")
    private setDisabled() {
        this.internals.ariaDisabled = `${this.disabled}`;
    }

    private handleInput = async () => {
        this.value = this.internalInput.value;
        this.uxInput.emit(this.value);
        this.internals.setFormValue(this.value);
    };

    private handleChange = async () => {
        this.value = this.internalInput.value;
        this.validate();
        this.uxInputChange.emit(this.value);
        this.internals.setFormValue(this.value);
    };

    private handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
            this.internals.form?.requestSubmit();
        }
    };

    private handleIconClick = (e: MouseEvent) => {
        this.uxIconClick.emit(e);
    };

    private clearInput = (e: MouseEvent) => {
        const event = this.uxClear.emit(e);
        if (!event.defaultPrevented) {
            this.value = "";
            this.internalInput.focus();
        }
    };

    private validate = (customError: boolean = false): void => {
        const validity = getValidity(this.internalInput, customError);
        this.validity = validity;
        this.internals.setValidity(validity, this.getValidationMessage(), this.internalInput);
    };

    private updateHasUnit = () => {
        if (this.host.querySelector("ux-unit[slot='unit']")) {
            this.hasUnit = true;
        }
    };

    private checkUnitSlot = () => {
        // @ts-expect-error Check for typing
        const unitSlot = this.host.shadowRoot.querySelector("slot[name='unit']") as HTMLSlotElement;
        unitSlot.addEventListener("slotchange", this.updateHasUnit);
    };

    componentDidLoad(): void {
        limitSlotElements(this.host, {
            allowed: {
                icon: ["ux-icon"],
                unit: ["ux-unit"]
            },
            disallowed: {
                "": ["ux-unit"]
            }
        });
        this.checkUnitSlot();
        this.internals.form?.addEventListener("reset", () => this.validate());
        this.internals.form?.addEventListener("submit", () => this.validate());
    }

    componentWillLoad(): void {
        this.internals.setFormValue(this.value);
        this.setValidity();
        this.updateHasUnit();
        this.setRequired();
        this.setDisabled();
    }

    render() {
        const showClearable = !this.disabled && this.clearable && this.value.length > 0 && !this.hasUnit;

        return (
            <Host focused={this.hasFocus}>
                <label htmlFor={this.inputId} part="label">
                    {this.label}
                    <slot /> {this.required ? <span class="required"></span> : null}
                </label>
                <div class="hint" part="hint">
                    <slot name="hint"></slot>
                </div>
                <div class="error-message">
                    <slot name="error"></slot>
                </div>
                <div
                    class={{
                        controls: true,
                        "has-unit": this.hasUnit
                    }}
                >
                    <input
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.internalInput = el)}
                        type={this.type}
                        placeholder={this.placeholder}
                        name={this.name}
                        id={this.inputId}
                        disabled={this.disabled}
                        required={this.required}
                        readonly={this.readonly}
                        minlength={this.minlength}
                        maxlength={this.maxlength}
                        max={this.max}
                        min={this.min}
                        step={this.step}
                        value={this.value}
                        spellcheck={this.spellcheck}
                        autocapitalize={this.autocapitalize}
                        autocomplete={this.autocomplete}
                        autofocus={this.autofocus}
                        inputmode={this.inputmode}
                        onInput={() => this.handleInput()}
                        onChange={() => this.handleChange()}
                        onBlur={(e) => this.handleBlur(e)}
                        onFocus={() => this.handleFocus()}
                        onKeyPress={(e) => this.handleKeyPress(e)}
                    />
                    <div class="unit">
                        <slot name="unit" />
                    </div>
                    {!showClearable && (
                        <div class="icon" onClick={this.handleIconClick}>
                            <slot name="icon" />
                        </div>
                    )}
                    {showClearable ? (
                        <button type="button" class="clear" onClick={this.clearInput}>
                            <ux-icon name="ux-input-clearable" />
                        </button>
                    ) : null}
                </div>
            </Host>
        );
    }
}
